'use strict';

const api = require('../../helpers/api');
const helper = require('../../helpers/helper');

/**
 * The class represents the Apple pay functionality
 */
class ApplePayBase {
    constructor() {
        this.$applePayTab = document.querySelector?.('.js-nav-item-apple-pay');
        this.$applePayButtonWrap = document.querySelector(
            '.js-applepay-pdp-button-wrap, .js-applepay-cart-button-wrap, .js-applepay-billing-button-wrap');
        this.$applePayBtn = document.getElementById('apple-pay-btn');
        this.$continueButton = document.querySelector?.('button.submit-payment');
        this.$applePayBtnContainer = document.querySelector('.js-applepay-button');

        this.applePay = paypal.Applepay();

        this.applePayConfigs = helper.tryParseJSON(this.$applePayBtnContainer.getAttribute('data-configs'));
        this.isProductPage = [window.paypalConstants.PAGE_FLOW_PDP, window.paypalConstants.PAGE_FLOW_PVP].includes(this.applePayConfigs.flow);
    }

    /**
     * Initiates the Apple pay functionality
     */
    async initApplePay() {
        const applePayConfig = await this.applePay.config();

        if (applePayConfig.isEligible) {
            this.renderApplePay();

            this.$applePayBtn.addEventListener('click', () => {
                this.onClick(applePayConfig);
            });
        }
    }

    /**
     * On Apple pay button click
     * @param {ApplePayConfig} applePayConfig An Apple pay config object generated by PayPal
     */
    onClick(applePayConfig) {
        this.saveApplePayTotalData();
        this.createApplePaySession(applePayConfig);
    }

    /**
    * Adds shipping methods based on the address
    * @param {Object} sessionEvent Apple Pay session event
    * @param {Object} applePayConfigs Apple Pay config object
    * @returns {Promise} Promise with applicable shipping options
    */
    async onShippingContactSelected(sessionEvent, applePayConfigs) {
        const shippingAddress = sessionEvent.shippingContact;
        const city = shippingAddress.locality;
        const stateCode = shippingAddress.administrativeArea;
        const countryCode = shippingAddress.countryCode;
        const postalCode = shippingAddress.postalCode;
        const addressQueryString = `city=${city}&stateCode=${stateCode}&countryCode=${countryCode}&postalCode=${postalCode}`;

        const url = `${applePayConfigs.getApplicableShippingOptionsUrl}?${addressQueryString}`;

        const result = await helper.getApplicableShippingOptions(url);

        const shippingContactSelectedUpdate = {
            newTotal: {
                label: this.applePayConfigs.applePayLabel,
                type: this.applePayConfigs.applePayTotalType,
                amount: this.amount
            }
        };

        if (result.applicableShippingMethods) {
            shippingContactSelectedUpdate.newShippingMethods = result.applicableShippingMethods;
        } else if (result.error) {
            shippingContactSelectedUpdate.errors = this.createApplePayErrors(result.errors);
            shippingContactSelectedUpdate.newShippingMethods = [];
        }

        return shippingContactSelectedUpdate;
    }

    /**
    * Creates an array of ApplePayErrors for Apple Pay pop-up
    * @param {Array} errorsData List of error objects with error field and message
    * @returns {Array} An array of ApplePayErrors
    */
    createApplePayErrors(errorsData) {
        const errors = [];

        errorsData.forEach(function(errorData) {
            const errorCode = errorData.errorCode;
            const field = errorData.contactField;
            const fieldName = errorData.errorMessage;

            const applePayError = new ApplePayError(errorCode, field, fieldName);

            errors.push(applePayError);
        });

        return errors;
    }

    /**
    * Clears basket on product page if Apple Pay pop-up is closed
    * @param {boolean} isProductPage whether it is product page
    */
    clearBasketForProductPage(isProductPage) {
        if (isProductPage) {
            helper.removeAllProductsFromCart();
        }
    }

    /**
    * Adds updated total and lineitems based on shipping option
    * @param {Object} sessionEvent Apple Pay session event
    * @param {Object} applePayConfigs Apple Pay config object
    * @returns {Promise} Promise with updated data for selected shipping option
    */
    async onShippingMethodSelected(sessionEvent, applePayConfigs) {
        const newShippingMethod = sessionEvent.shippingMethod;
        const shippingOptionId = newShippingMethod.identifier;

        const shippingOptionData = await helper.updateAmountForShippingOption(shippingOptionId, applePayConfigs.getAmountForShippingOptionUrl);

        const updatedAmount = shippingOptionData.amount;
        const updatedTax = shippingOptionData.totalTax;

        const newTotal = {
            label: applePayConfigs.applePayLabel,
            type: applePayConfigs.applePayTotalType,
            amount: updatedAmount
        };

        return {
            newTotal: newTotal,
            newLineItems: [{
                label: applePayConfigs.lineItemsLabels.subTotal,
                type: applePayConfigs.applePayTotalType,
                amount: this.subtotal
            },
            {
                label: applePayConfigs.lineItemsLabels.shipping,
                type: applePayConfigs.applePayTotalType,
                amount: newShippingMethod.amount
            },
            {
                label: applePayConfigs.lineItemsLabels.tax,
                type: applePayConfigs.applePayTotalType,
                amount: updatedTax
            }],
            status: 'STATUS_SUCCESS'
        };
    }

    /**
     * Creates an Apple pay session and process an order
     * @param {ApplePayConfig} applePayConfig An Apple pay config object generated by PayPal
     */
    createApplePaySession(applePayConfig) {
        const AlertHandlerModel = require('../alertHandler');
        const alertHandlerInstance = new AlertHandlerModel();

        const paymentRequest = {
            countryCode: applePayConfig.countryCode,
            merchantCapabilities: applePayConfig.merchantCapabilities,
            supportedNetworks: applePayConfig.supportedNetworks,
            currencyCode: this.currencyCode,
            requiredShippingContactFields: this.applePayConfigs.requiredShippingContactFields,
            requiredBillingContactFields: this.applePayConfigs.requiredBillingContactFields,
            total: {
                label: this.applePayConfigs.applePayLabel,
                type: this.applePayConfigs.applePayTotalType,
                amount: this.amount
            },
            lineItems: [{
                label: this.applePayConfigs.lineItemsLabels.subTotal,
                type: this.applePayConfigs.applePayTotalType,
                amount: this.subtotal
            },
            {
                label: this.applePayConfigs.lineItemsLabels.shipping,
                type: this.applePayConfigs.applePayTotalType,
                amount: this.shipping
            },
            {
                label: this.applePayConfigs.lineItemsLabels.tax,
                type: this.applePayConfigs.applePayTotalType,
                amount: this.tax
            }]
        };

        const session = new ApplePaySession(this.applePayConfigs.applePayVersion, paymentRequest);

        session.onvalidatemerchant = (event) => {
            this.applePay
                .validateMerchant({
                    validationUrl: event.validationURL
                })
                .then((payload) => {
                    session.completeMerchantValidation(payload.merchantSession);
                })
                .catch(() => {
                    this.clearBasketForProductPage(this.isProductPage);

                    alertHandlerInstance.showError(this.applePayConfigs.errorMessages.applePayMerchantVerificationFailed);
                    session.abort();
                });
        };

        session.onpaymentmethodselected = () => {
            session.completePaymentMethodSelection({
                newTotal: paymentRequest.total,
                newLineItems: [{
                    label: this.applePayConfigs.lineItemsLabels.subTotal,
                    type: this.applePayConfigs.applePayTotalType,
                    amount: this.subtotal
                },
                {
                    label: this.applePayConfigs.lineItemsLabels.shipping,
                    type: this.applePayConfigs.applePayTotalType,
                    amount: this.shipping
                },
                {
                    label: this.applePayConfigs.lineItemsLabels.tax,
                    type: this.applePayConfigs.applePayTotalType,
                    amount: this.tax
                }]
            });
        };

        session.onshippingcontactselected = (event) => {
            this.onShippingContactSelected(event, this.applePayConfigs)
                .then((applePayShippingContactUpdate) => {
                    session.completeShippingContactSelection(
                        applePayShippingContactUpdate
                    );
                })
                .catch(() => {
                    session.abort();
                });
        };

        // Updates price based on a selected shipping method
        session.onshippingmethodselected = (event) => {
            this.onShippingMethodSelected(event, this.applePayConfigs)
                .then((applePayShippingMethodUpdate) => {
                    session.completeShippingMethodSelection(applePayShippingMethodUpdate);
                })
                .catch(() => {
                    session.abort();
                });
        };

        session.onpaymentauthorized = async(event) => {
            this.shippingContact = event.payment.shippingContact;
            // Saves an email and phone number from the Apple pay to pass it on backend
            this.email = this.shippingContact.emailAddress;
            this.phoneNumber = this.shippingContact.phoneNumber;
            this.billingContact = event.payment.billingContact;

            if (this.applePayConfigs.flow === window.paypalConstants.PAGE_FLOW_BILLING) {
                document.getElementById('applepay-email-address').value = this.email;
                document.getElementById('applepay-phone-number').value = this.phoneNumber;
            } else {
                this.shippingAddressAsString = JSON.stringify(this.shippingContact);
            }

            const paymentSource = {
                apple_pay: {
                    id: event.payment.token.transactionIdentifier,
                    email_address: this.email
                },
                billingAddress: {
                    name: `${this.billingContact.givenName} ${this.billingContact.familyName}`,
                    phone: {
                        phone_number: {
                            national_number: this.phoneNumber
                        }
                    },
                    email_address: this.email,
                    address: {
                        address_line_1: this.billingContact.addressLines[0],
                        admin_area_2: this.billingContact.administrativeArea,
                        admin_area_1: this.billingContact.locality,
                        postal_code: this.billingContact.postalCode,
                        country_code: this.billingContact.countryCode
                    }
                },
                pmName: window.paypalConstants.PAYMENT_METHOD_ID_APPLE_PAY
            };

            if (!this.applePayConfigs.payNowEnabled) {
                paymentSource.shippingAddress = {
                    name: `${this.shippingContact.givenName} ${this.shippingContact.familyName}`,
                    addressLines: this.shippingContact.addressLines ? this.shippingContact.addressLines[0] : null
                };
            }

            api.createApplePayOrder(paymentSource)
                .then(data => {
                    if (data.error) {
                        session.completePayment({
                            status: ApplePaySession.STATUS_FAILURE,
                            errors: this.createApplePayErrors(data.errors)
                        });
                    } else {
                        this.applePay.confirmOrder({
                            orderId: data.id,
                            token: event.payment.token,
                            billingContact: event.payment.billingContact
                        })
                            .then(confirmResult => {
                                session.completePayment(ApplePaySession.STATUS_SUCCESS);

                                if (this.applePayConfigs.payNowEnabled) {
                                    api.finishPayNowPayment(paymentSource);
                                } else {
                                    this.processGenericApplePayFlow(this.applePayConfigs.flow, confirmResult);
                                }
                            })
                            .catch(() => {
                                this.clearBasketForProductPage(this.isProductPage);

                                alertHandlerInstance.showError(this.applePayConfigs.errorMessages.applePayPaymentMethodVerificationFailed);
                                session.completePayment(ApplePaySession.STATUS_FAILURE);
                            });
                    }
                });
        };

        session.oncancel = () => {
            this.clearBasketForProductPage(this.isProductPage);

            alertHandlerInstance.showError(this.applePayConfigs.errorMessages.applePayCancelled);
        };

        session.begin();
    }

    /**
     * Process generic (non-paynow) Apple Pay flow
     * @param {string} pageFlow name of the page where Apple Pay button is shown
     * @param {Object} confirmResult result of Apple Pay order
     */
    processGenericApplePayFlow(pageFlow, confirmResult) {
        if (this.applePayConfigs.flow === window.paypalConstants.PAGE_FLOW_BILLING) {
            // Saves the Apple pay payment source data to pass it on backend
            document.getElementById('applepay-payment-source').value = JSON.stringify(
                confirmResult.approveApplePayPayment.payment_source
            );

            this.$continueButton.click();
        } else {
            this.paymentSource = JSON.stringify(confirmResult.approveApplePayPayment.payment_source);

            this.handleNonBillingApplePayFlow();
        }
    }

    /**
     * Handles the Apple pay from the Cart or PDP page
     */
    handleNonBillingApplePayFlow() {
        const cartFormData = api.createCartBillingFormData({
            applePayEmailAddress: this.email,
            applePayPhoneNumber: this.phoneNumber,
            applePayPaymentSource: this.paymentSource,
            applePayShippingAddressAsString: this.shippingAddressAsString,
            usedPaymentMethod: window.paypalConstants.PAYMENT_METHOD_ID_APPLE_PAY,
            paymentMethod: window.paypalConstants.PAYMENT_METHOD_ID_PAYPAL
        }, this.$applePayBtnContainer);

        fetch(helper.getUrlWithCsrfToken(window.paypalUrls.returnFromCart), {
            method: 'POST',
            body: cartFormData
        })
            .then((response) => response.json())
            .then((data) => {
                helper.returnFromCartRedirect(data);
            });
    }

    /**
     * Saves the actual amount and currency code
     */
    saveApplePayTotalData() {
        $.ajax({
            url: helper.getUrlWithCsrfToken(window.paypalUrls.getBasketData),
            async: false,
            type: 'get',
            dataType: 'json',
            success: data => {
                this.amount = data.amount;
                this.subtotal = data.subtotal;
                this.shipping = data.shipping;
                this.tax = data.tax;
                this.currencyCode = data.currencyCode;
            },
            error: () => {
                window.location.reload();
            }
        });
    }

    /**
     * Shows an Apple Pay button and tab
     */
    renderApplePay() {
        // Shows an Apple pay container
        this.$applePayButtonWrap.classList.remove('d-none');

        // Shows an Apple pay tab on the billing page
        if (this.$applePayTab) {
            this.$applePayTab.classList.remove('d-none');
        }
    }
}

module.exports = ApplePayBase;
